import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, CheckCheck } from "lucide-react";

// Each scene = a short real-time exchange in a different company area.
const SCENES = [
  {
    area: "Comercial",
    msgs: [
      { who: "them", name: "Rafael · Comercial", text: "Isa, qual a condição de pagamento pro cliente Vale?" },
      { who: "typing" },
      { who: "isa", text: "Boleto 30/60/90 e 3% à vista. Quer que eu gere a proposta? 📄" },
      { who: "them", name: "Rafael · Comercial", text: "Gera aí 🙌" },
      { who: "typing" },
      { who: "isa", text: "Proposta #4821 gerada e enviada ✅" },
    ],
  },
  {
    area: "RH",
    msgs: [
      { who: "them", name: "Marina · RH", text: "Isa, quantos dias de férias a Ana ainda tem?" },
      { who: "typing" },
      { who: "isa", text: "A Ana tem 12 dias disponíveis, vencem em 30/09. 🗓️" },
      { who: "them", name: "Marina · RH", text: "Fecha! Agenda pra dia 15." },
      { who: "typing" },
      { who: "isa", text: "Agendado e comunicado ao gestor ✅" },
    ],
  },
  {
    area: "Financeiro",
    msgs: [
      { who: "them", name: "Bia · Financeiro", text: "Isa, o boleto da Vale já compensou?" },
      { who: "typing" },
      { who: "isa", text: "Compensou hoje 09:12 — R$ 48.500 💰" },
      { who: "them", name: "Bia · Financeiro", text: "Perfeito, obrigada!" },
    ],
  },
  {
    area: "Jurídico",
    msgs: [
      { who: "them", name: "Caio · Jurídico", text: "Isa, o contrato da Vale tem cláusula de reajuste?" },
      { who: "typing" },
      { who: "isa", text: "Sim — IPCA anual, cláusula 7.2. Te mando o trecho? 📑" },
      { who: "them", name: "Caio · Jurídico", text: "Manda!" },
      { who: "typing" },
      { who: "isa", text: "Enviei o trecho destacado ✅" },
    ],
  },
];

function Bubble({ m }) {
  const mine = m.who === "isa";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className={`flex ${mine ? "justify-start" : "justify-end"}`}
    >
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[12.5px] leading-snug shadow-sm ${mine ? "rounded-tl-sm bg-white text-slate-800" : "rounded-tr-sm bg-[#D9FDD3] text-slate-800"}`}>
        {!mine && m.name && <p className="mb-0.5 text-[10px] font-semibold text-[#0b1220]/70">{m.name}</p>}
        {mine && <p className="mb-0.5 text-[10px] font-semibold text-emerald-600">Isa</p>}
        <p>{m.text}</p>
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[9px] text-slate-400">
          {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          {!mine && <CheckCheck size={11} className="text-sky-500" />}
        </div>
      </div>
    </motion.div>
  );
}

function Typing() {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex justify-start">
      <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm bg-white px-3 py-2.5 shadow-sm">
        {[0, 1, 2].map((i) => (
          <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-slate-400"
            animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
            transition={{ duration: 1, repeat: Infinity, delay: i * 0.18 }} />
        ))}
      </div>
    </motion.div>
  );
}

export default function IsaChatMockup() {
  const [scene, setScene] = useState(0);
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const timers = [];
    const wait = (ms) => new Promise((r) => timers.push(setTimeout(r, ms)));
    let idx = 0;

    async function run() {
      while (alive) {
        const sc = SCENES[idx];
        setScene(idx);
        setShown([]); setTyping(false);
        await wait(650);
        for (const step of sc.msgs) {
          if (!alive) return;
          if (step.who === "typing") { setTyping(true); await wait(1250); setTyping(false); }
          else { setShown((s) => [...s, step]); await wait(step.who === "isa" ? 1350 : 1000); }
        }
        await wait(2400);
        idx = (idx + 1) % SCENES.length;
      }
    }
    run();
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [shown, typing]);

  return (
    <div className="relative h-[30rem] w-64 shrink-0 rounded-[2.75rem] border-[7px] border-slate-900 bg-slate-900 shadow-[0_30px_60px_-15px_rgba(11,18,32,0.45)]">
      <div className="absolute left-1/2 top-2 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-slate-900" />
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[2.15rem] bg-[#EFEAE2]">
        <div className="flex items-center gap-2.5 bg-[#0b1220] px-4 pb-2.5 pt-6 text-white">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold">I</div>
          <div className="flex-1 leading-tight">
            <p className="text-[13px] font-semibold">Isa</p>
            <p className="flex items-center gap-1 text-[10px] text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> online agora</p>
          </div>
          <AnimatePresence mode="wait">
            <motion.span key={scene} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
              className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/90">
              {SCENES[scene].area}
            </motion.span>
          </AnimatePresence>
        </div>
        <div ref={scrollRef} className="flex-1 space-y-2 overflow-hidden px-3 py-3"
          style={{ backgroundImage: "radial-gradient(rgba(11,18,32,0.04) 1px, transparent 1px)", backgroundSize: "16px 16px" }}>
          {shown.map((m, i) => <Bubble key={`${scene}-${i}`} m={m} />)}
          <AnimatePresence>{typing && <Typing />}</AnimatePresence>
        </div>
        <div className="flex items-center gap-2 bg-[#EFEAE2] px-3 pb-3 pt-1">
          <div className="flex-1 rounded-full bg-white px-3 py-2 text-[11px] text-slate-400 shadow-sm">Mensagem</div>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm"><Check size={15} /></div>
        </div>
      </div>
    </div>
  );
}
